/*
 * LABAREAU landing-page sections (lp-*): shared behaviour.
 * Vanilla JS, no dependencies. Everything degrades: buy forms post natively,
 * the before/after slider renders at 50 %, the sticky bar simply stays hidden.
 */
(function () {
  'use strict';

  var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
   * Buy forms: add via /cart/add.js, then redirect (checkout / cart /
   * discount URL). On any failure, let the form submit natively so the
   * no-JS path (/cart/add + return_to) takes over.
   * ---------------------------------------------------------------- */
  function initBuyForms(scope) {
    var forms = (scope || document).querySelectorAll('form[data-lp-buy-form]:not([data-lp-ready])');
    Array.prototype.forEach.call(forms, function (form) {
      form.setAttribute('data-lp-ready', '');
      form.addEventListener('submit', function (event) {
        if (!window.fetch || !window.FormData) return;
        event.preventDefault();
        var button = form.querySelector('[type="submit"]');
        var redirect = form.getAttribute('data-redirect') || root + 'checkout';
        var items = collectItems(form);
        if (!items.length) {
          form.submit();
          return;
        }
        form.removeAttribute('data-error');
        if (button) {
          button.classList.add('is-loading');
          button.setAttribute('aria-busy', 'true');
        }
        fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('cart/add failed: ' + response.status);
            return response.json();
          })
          .then(function () {
            document.dispatchEvent(new CustomEvent('lp:added', { detail: { items: items, form: form } }));
            window.location.assign(redirect);
          })
          .catch(function () {
            if (button) {
              button.classList.remove('is-loading');
              button.removeAttribute('aria-busy');
            }
            form.setAttribute('data-error', '');
            var error = form.querySelector('.lp-buy-form__error');
            if (error) error.focus && error.focus();
            // Native fallback after a short pause so the error is visible to AT.
            window.setTimeout(function () {
              form.submit();
            }, 1200);
          });
      });
    });
  }

  function collectItems(form) {
    var items = [];
    // form.elements also covers inputs linked with the form="…" attribute (offer variant radios).
    var primary = form.elements.namedItem('id');
    var primaryValue = primary && primary.value;
    if (primaryValue) {
      items.push({ id: parseInt(primaryValue, 10), quantity: parseInt(form.getAttribute('data-quantity') || '1', 10) || 1 });
    }
    var extras = form.elements.namedItem('lp_extra_id');
    if (extras) {
      var list = typeof extras.length === 'number' && !extras.tagName ? extras : [extras];
      Array.prototype.forEach.call(list, function (input) {
        if (input.value) items.push({ id: parseInt(input.value, 10), quantity: 1 });
      });
    }
    return items;
  }

  /* ------------------------------------------------------------------
   * Offer variant toggle: keep the visible price in sync with the
   * selected radio (the radio itself is name="id", so no JS is needed
   * for the actual add-to-cart).
   * ---------------------------------------------------------------- */
  function initVariantPickers(scope) {
    var pickers = (scope || document).querySelectorAll('[data-lp-variant-picker]:not([data-lp-ready])');
    Array.prototype.forEach.call(pickers, function (picker) {
      picker.setAttribute('data-lp-ready', '');
      var section = picker.closest('[data-lp-offer]') || picker.closest('.lp-section') || document;
      var priceEls = section.querySelectorAll('[data-lp-price]');
      var totalEls = section.querySelectorAll('[data-lp-total]');
      var extraTotal = parseInt(section.getAttribute('data-lp-extra-total') || '0', 10) || 0;
      var money = section.getAttribute('data-lp-money-format') || '€{{amount_with_comma_separator}}';

      picker.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || input.name !== 'id') return;
        var cents = parseInt(input.getAttribute('data-price') || '0', 10);
        var formatted = input.getAttribute('data-price-formatted') || formatMoney(cents, money);
        Array.prototype.forEach.call(priceEls, function (el) {
          el.textContent = formatted;
        });
        Array.prototype.forEach.call(totalEls, function (el) {
          el.textContent = formatMoney(cents + extraTotal, money);
        });
      });
    });
  }

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var intPart = parts[0];
    var dec = parts[1];
    if (format.indexOf('amount_no_decimals') !== -1) return format.replace(/\{\{.*?\}\}/, Math.round(cents / 100));
    if (format.indexOf('amount_with_comma_separator') !== -1) {
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return format.replace(/\{\{.*?\}\}/, intPart + ',' + dec);
    }
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{.*?\}\}/, intPart + '.' + dec);
  }

  /* ------------------------------------------------------------------
   * Sticky add-to-cart bar: visible once the trigger (hero CTA) has
   * scrolled above the viewport. Hidden again near the trigger or when
   * the final CTA / offer box (data-lp-sticky-hide) is on screen.
   * ---------------------------------------------------------------- */
  function initSticky() {
    var bars = document.querySelectorAll('[data-lp-sticky]:not([data-lp-ready])');
    if (!bars.length) return;
    Array.prototype.forEach.call(bars, function (bar) {
      bar.setAttribute('data-lp-ready', '');
      var triggerSelector = bar.getAttribute('data-trigger') || '[data-lp-sticky-trigger]';
      var trigger = document.querySelector(triggerSelector);
      var hiders = document.querySelectorAll('[data-lp-sticky-hide]');
      var pastTrigger = !trigger;
      var hiderVisible = false;

      function update() {
        var show = pastTrigger && !hiderVisible;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        document.body.classList.toggle('lp-sticky-visible', show);
        if (show) {
          document.documentElement.style.setProperty('--lp-sticky-height', bar.offsetHeight + 'px');
        }
      }

      if (!('IntersectionObserver' in window)) {
        // Fallback: show after a scroll past 60 % of the viewport.
        window.addEventListener(
          'scroll',
          function () {
            pastTrigger = window.scrollY > window.innerHeight * 0.6;
            update();
          },
          { passive: true }
        );
        return;
      }

      if (trigger) {
        new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              pastTrigger = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            });
            update();
          },
          { threshold: 0 }
        ).observe(trigger);
      }

      if (hiders.length) {
        var visible = new Set();
        var hiderObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) visible.add(entry.target);
              else visible.delete(entry.target);
            });
            hiderVisible = visible.size > 0;
            update();
          },
          { threshold: 0.2 }
        );
        Array.prototype.forEach.call(hiders, function (el) {
          hiderObserver.observe(el);
        });
      }
      update();
    });
  }

  /* ------------------------------------------------------------------
   * Before/after comparison slider (range input drives a CSS var).
   * ---------------------------------------------------------------- */
  function initCompare(scope) {
    var sliders = (scope || document).querySelectorAll('[data-lp-compare]:not([data-lp-ready])');
    Array.prototype.forEach.call(sliders, function (wrap) {
      wrap.setAttribute('data-lp-ready', '');
      var range = wrap.querySelector('input[type="range"]');
      if (!range) return;
      var set = function () {
        wrap.style.setProperty('--lp-ba-pos', range.value + '%');
        range.setAttribute('aria-valuenow', range.value);
      };
      range.addEventListener('input', set);
      set();
    });
  }

  /* ------------------------------------------------------------------
   * FAQ: optional "one open at a time".
   * ---------------------------------------------------------------- */
  function initFaq(scope) {
    var lists = (scope || document).querySelectorAll('[data-lp-faq-single]:not([data-lp-ready])');
    Array.prototype.forEach.call(lists, function (list) {
      list.setAttribute('data-lp-ready', '');
      list.addEventListener('toggle', function (event) {
        var target = event.target;
        if (!target.open) return;
        Array.prototype.forEach.call(list.querySelectorAll('details[open]'), function (details) {
          if (details !== target) details.open = false;
        });
      }, true);
    });
  }

  /* ------------------------------------------------------------------
   * Smooth anchor scrolling for CTA links that jump to the offer box.
   * ---------------------------------------------------------------- */
  function initAnchors(scope) {
    var links = (scope || document).querySelectorAll('a[data-lp-scroll]:not([data-lp-ready])');
    Array.prototype.forEach.call(links, function (link) {
      link.setAttribute('data-lp-ready', '');
      link.addEventListener('click', function (event) {
        var id = (link.getAttribute('href') || '').split('#')[1];
        var target = id && document.getElementById(id);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
  }

  function initAll(scope) {
    initBuyForms(scope);
    initVariantPickers(scope);
    initCompare(scope);
    initFaq(scope);
    initAnchors(scope);
    initSticky();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor: re-init when a section is (re)loaded.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
